import { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Zap, Plug, Cable, AlertTriangle, Check, Power, CircuitBoard, Eye, EyeOff, Shuffle, RotateCcw } from 'lucide-react';
import { ITEMS } from '@/config/constants';
import LeonDialogue from '@/components/LeonDialogue';
import CONFIG from '@/config/siteConfig';
import { PUZZLE_5_WIRE_PUZZLE } from '@/config/missionContent';

const WIRE_COLORS = [
  { name: 'Red', color: '#ef4444', glow: 'rgba(239,68,68,0.4)', bg: 'rgba(239,68,68,0.1)' },
  { name: 'Blue', color: '#3b82f6', glow: 'rgba(59,130,246,0.4)', bg: 'rgba(59,130,246,0.1)' },
  { name: 'Green', color: '#22c55e', glow: 'rgba(34,197,94,0.4)', bg: 'rgba(34,197,94,0.1)' },
  { name: 'Yellow', color: '#eab308', glow: 'rgba(234,179,8,0.4)', bg: 'rgba(234,179,8,0.1)' },
  { name: 'Purple', color: '#a855f7', glow: 'rgba(168,85,247,0.4)', bg: 'rgba(168,85,247,0.1)' },
];

const PREVIEW_DURATION = 3;
const MAX_CONSECUTIVE_MISSES = 3;

const shuffle = (arr) => {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
};

/* ── CircuitBackground ─────────────────────────────────── */
function CircuitBackground() {
  const particles = useMemo(() =>
    Array.from({ length: 10 }, (_, i) => ({
      id: i,
      left: `${8 + (i * 9.3) % 84}%`,
      top: `${5 + (i * 13.7) % 90}%`,
      size: 2 + (i % 3),
      dur: 3 + (i % 4),
      delay: i * 0.4,
      color: i % 3 === 0 ? '#00ff41' : '#22d3ee',
    })), []);

  const traces = useMemo(() =>
    Array.from({ length: 12 }, (_, i) => ({
      id: i,
      x1: (i * 37 + 10) % 400,
      y1: (i * 53 + 20) % 400,
      x2: (i * 29 + 180) % 400,
      y2: (i * 47 + 100) % 400,
      dur: 3 + (i % 3),
      delay: i * 0.3,
    })), []);

  return (
    <div className="fixed inset-0 overflow-hidden pointer-events-none">
      <div className="absolute inset-0 bg-gradient-to-b from-black via-[#010a05] to-black" />
      {particles.map(p => (
        <motion.div
          key={p.id}
          className="absolute rounded-full"
          style={{
            left: p.left, top: p.top,
            width: p.size, height: p.size,
            background: p.color,
            boxShadow: `0 0 ${p.size * 3}px ${p.color}`,
          }}
          animate={{ opacity: [0, 0.8, 0], y: [0, -20, 0] }}
          transition={{ duration: p.dur, delay: p.delay, repeat: Infinity, ease: 'easeInOut' }}
        />
      ))}
      <div className="absolute inset-0 opacity-[0.04]">
        <svg className="absolute inset-0 w-full h-full" viewBox="0 0 400 400">
          {traces.map(t => (
            <motion.line
              key={t.id}
              x1={t.x1} y1={t.y1} x2={t.x2} y2={t.y2}
              stroke="#00ff41" strokeWidth="0.5"
              initial={{ pathLength: 0 }}
              animate={{ pathLength: [0, 1, 0] }}
              transition={{ duration: t.dur, delay: t.delay, repeat: Infinity }}
            />
          ))}
        </svg>
      </div>
      <motion.div
        className="absolute inset-0"
        animate={{ opacity: [0.15, 0.3, 0.15] }}
        transition={{ duration: 5, repeat: Infinity, ease: 'easeInOut' }}
        style={{ background: 'radial-gradient(ellipse at center, transparent 40%, rgba(0,40,10,0.6) 100%)' }}
      />
      <div
        className="absolute inset-0 opacity-[0.03]"
        style={{
          backgroundImage: 'repeating-linear-gradient(0deg, transparent, transparent 2px, rgba(0,255,65,0.08) 2px, rgba(0,255,65,0.08) 4px)',
          backgroundSize: '100% 4px',
        }}
      />
    </div>
  );
}

/* ── WireTutorial ──────────────────────────────────────── */
function WireTutorial({ onStart }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      className="relative z-10 max-w-md mx-auto"
    >
      <div className="bg-black/70 border border-green-800/40 rounded-lg p-6 backdrop-blur-sm">
        <div className="flex items-center gap-3 mb-5">
          <div className="w-10 h-10 rounded-lg bg-green-900/40 border border-green-600/30 flex items-center justify-center">
            <CircuitBoard size={20} className="text-green-400" />
          </div>
          <div>
            <h3 className="font-orbitron text-green-400 text-sm tracking-wider">POWER GRID PROTOCOL</h3>
            <p className="text-xs font-terminal text-green-700 tracking-widest mt-0.5">EMERGENCY WIRE RESTORATION</p>
          </div>
        </div>

        <div className="mb-5">
          <p className="text-xs font-terminal text-green-600 tracking-[3px] mb-3">PROCEDURE</p>
          <div className="space-y-2.5">
            {[
              { n: '01', text: 'Memorize wire positions during the 3-SECOND PREVIEW' },
              { n: '02', text: 'Right-side colors will HIDE — match from memory' },
              { n: '03', text: 'Connect all 5 wires to restore power' },
            ].map(step => (
              <div key={step.n} className="flex items-start gap-3">
                <span className="text-xs font-orbitron text-green-500 mt-0.5">{step.n}</span>
                <p className="text-xs text-gray-300 font-rajdhani">{step.text}</p>
              </div>
            ))}
          </div>
        </div>

        <div className="mb-5">
          <p className="text-xs font-terminal text-green-600 tracking-[3px] mb-3">WIRE IDENTIFICATION</p>
          <div className="flex flex-wrap gap-3">
            {WIRE_COLORS.map(w => (
              <div key={w.name} className="flex items-center gap-1.5">
                <div className="w-2.5 h-2.5 rounded-full" style={{ background: w.color, boxShadow: `0 0 6px ${w.glow}` }} />
                <span className="text-xs font-terminal tracking-wider" style={{ color: w.color }}>{w.name.toUpperCase()}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Danger warnings */}
        <div className="space-y-2 mb-5">
          <div className="flex items-center gap-2 bg-yellow-950/20 border border-yellow-900/30 rounded px-3 py-2">
            <Shuffle size={12} className="text-yellow-400 shrink-0" />
            <span className="text-xs font-terminal text-yellow-400 tracking-wider">WRONG MATCH SCRAMBLES PORT POSITIONS</span>
          </div>
          <div className="flex items-center gap-2 bg-red-950/20 border border-red-900/30 rounded px-3 py-2">
            <RotateCcw size={12} className="text-red-400 shrink-0" />
            <span className="text-xs font-terminal text-red-400 tracking-wider">3 CONSECUTIVE MISSES = FULL RESET</span>
          </div>
        </div>

        <motion.button
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          onClick={onStart}
          className="w-full py-3 rounded font-orbitron text-sm tracking-widest text-black relative overflow-hidden"
          style={{ background: 'linear-gradient(135deg, #22c55e, #00ff41)' }}
        >
          <span className="relative z-10">RESTORE POWER</span>
          <motion.div
            className="absolute inset-0"
            animate={{ x: ['-100%', '200%'] }}
            transition={{ duration: 2, repeat: Infinity, ease: 'linear' }}
            style={{ background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.15), transparent)', width: '50%' }}
          />
        </motion.button>
      </div>
    </motion.div>
  );
}

/* ── HUDCorners ────────────────────────────────────────── */
function HUDCorners() {
  return (
    <>
      <div className="absolute top-2 left-2 w-4 h-4 border-t-2 border-l-2 border-green-800/30" />
      <div className="absolute top-2 right-2 w-4 h-4 border-t-2 border-r-2 border-green-800/30" />
      <div className="absolute bottom-2 left-2 w-4 h-4 border-b-2 border-l-2 border-green-800/30" />
      <div className="absolute bottom-2 right-2 w-4 h-4 border-b-2 border-r-2 border-green-800/30" />
    </>
  );
}

/* ── WireHUD ───────────────────────────────────────────── */
function WireHUD({ connected, total, mismatches, consecutiveMisses, previewTime, colorsHidden }) {
  const percentage = (connected / total) * 100;
  return (
    <div className="max-w-md mx-auto mb-5 relative z-10">
      <div className="flex items-center justify-between bg-black/40 border border-green-900/30 rounded px-4 py-2">
        <div className="flex items-center gap-2">
          {previewTime > 0 ? (
            <>
              <Eye size={10} className="text-cyan-400" />
              <span className="text-xs font-terminal text-cyan-400 tracking-wider">
                MEMORIZE — {previewTime}s
              </span>
            </>
          ) : (
            <>
              {colorsHidden && <EyeOff size={10} className="text-red-400" />}
              <span className="text-xs font-terminal text-green-700 tracking-wider">CHECKPOINT 09 // POWER GRID</span>
            </>
          )}
        </div>
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-1.5">
            <Cable size={10} className="text-green-500" />
            <span className="text-xs font-orbitron text-green-400">{connected}<span className="text-gray-600">/{total}</span></span>
          </div>
          {mismatches > 0 && (
            <div className="flex items-center gap-1.5">
              <AlertTriangle size={10} className="text-red-400" />
              <span className="text-xs font-orbitron text-red-400">{mismatches}</span>
            </div>
          )}
          {/* Consecutive miss pips */}
          <div className="flex items-center gap-1">
            {Array.from({ length: MAX_CONSECUTIVE_MISSES }).map((_, i) => (
              <div
                key={i}
                className={`w-2 h-2 rounded-full border ${
                  i < consecutiveMisses
                    ? 'bg-red-500 border-red-400 shadow-[0_0_4px_rgba(239,68,68,0.6)]'
                    : 'bg-transparent border-gray-700'
                }`}
              />
            ))}
          </div>
        </div>
      </div>
      <div className="h-2 bg-black/60 mt-1 rounded-full overflow-hidden border border-green-900/20">
        <motion.div
          animate={{ width: `${percentage}%` }}
          transition={{ duration: 0.3 }}
          className="h-full rounded-full"
          style={{
            background: connected === total
              ? 'linear-gradient(90deg, #22c55e, #4ade80)'
              : 'linear-gradient(90deg, #eab308, #fbbf24)',
            boxShadow: connected === total
              ? '0 0 10px rgba(34,197,94,0.5)'
              : '0 0 8px rgba(234,179,8,0.4)'
          }}
        />
      </div>
    </div>
  );
}

/* ── Main Component ────────────────────────────────────── */
export default function Puzzle5WirePuzzle({ onComplete, onItemCollected }) {
  const [showLeon, setShowLeon] = useState(true);
  const [showTutorial, setShowTutorial] = useState(false);
  const [puzzleActive, setPuzzleActive] = useState(false);
  const [solved, setSolved] = useState(false);
  const [showFinalLeon, setShowFinalLeon] = useState(false);
  const [selectedLeft, setSelectedLeft] = useState(null);
  const [connections, setConnections] = useState([]);
  const [wrongPair, setWrongPair] = useState(null);
  const [successPhase, setSuccessPhase] = useState(0);
  const [mismatches, setMismatches] = useState(0);
  const [shaking, setShaking] = useState(false);
  const [rightOrder, setRightOrder] = useState(() => shuffle([0, 1, 2, 3, 4]));
  const [previewTime, setPreviewTime] = useState(0);
  const [colorsHidden, setColorsHidden] = useState(false);
  const [consecutiveMisses, setConsecutiveMisses] = useState(0);
  const [resetFlash, setResetFlash] = useState(false);
  const [scrambleFlash, setScrambleFlash] = useState(false);
  const [showProceed, setShowProceed] = useState(false);
  const previewTimerRef = useRef(null);

  /* Mount-reset */
  useEffect(() => {
    setShowLeon(true);
    setShowTutorial(false);
    setPuzzleActive(false);
    setSolved(false);
    setShowFinalLeon(false);
    setSelectedLeft(null);
    setConnections([]);
    setWrongPair(null);
    setSuccessPhase(0);
    setMismatches(0);
    setShaking(false);
    setRightOrder(shuffle([0, 1, 2, 3, 4]));
    setPreviewTime(0);
    setColorsHidden(false);
    setConsecutiveMisses(0);
    setResetFlash(false);
    setScrambleFlash(false);
    setShowProceed(false);
    return () => { if (previewTimerRef.current) clearInterval(previewTimerRef.current); };
  }, []);

  /* Start preview countdown when puzzle becomes active */
  const startPreview = useCallback(() => {
    setColorsHidden(false);
    setPreviewTime(PREVIEW_DURATION);
    let t = PREVIEW_DURATION;
    if (previewTimerRef.current) clearInterval(previewTimerRef.current);
    previewTimerRef.current = setInterval(() => {
      t -= 1;
      setPreviewTime(t);
      if (t <= 0) {
        clearInterval(previewTimerRef.current);
        previewTimerRef.current = null;
        setColorsHidden(true);
      }
    }, 1000);
  }, []);

  const handleLeonDismiss = useCallback(() => {
    setShowLeon(false);
    setShowTutorial(true);
  }, []);

  const handleTutorialStart = useCallback(() => {
    setShowTutorial(false);
    setPuzzleActive(true);
    startPreview();
  }, [startPreview]);

  const isLeftConnected = useCallback((i) => connections.some(c => c.left === i), [connections]);
  const isRightConnected = useCallback((i) => connections.some(c => c.right === i), [connections]);

  const handleLeftClick = useCallback((index) => {
    if (!puzzleActive || solved || isLeftConnected(index)) return;
    setSelectedLeft(index);
    setWrongPair(null);
  }, [puzzleActive, solved, isLeftConnected]);

  /* Scramble only unconnected right positions */
  const scrambleUnconnected = useCallback((currentOrder, currentConns) => {
    const connectedRightIndices = new Set(currentConns.map(c => c.right));
    const freeIndices = [];
    const freeValues = [];
    currentOrder.forEach((val, idx) => {
      if (!connectedRightIndices.has(idx)) {
        freeIndices.push(idx);
        freeValues.push(val);
      }
    });
    const shuffledValues = shuffle(freeValues);
    const newOrder = [...currentOrder];
    freeIndices.forEach((idx, i) => {
      newOrder[idx] = shuffledValues[i];
    });
    return newOrder;
  }, []);

  /* Full reset — wipe all connections, re-scramble everything */
  const doFullReset = useCallback(() => {
    setConnections([]);
    setSelectedLeft(null);
    setWrongPair(null);
    setConsecutiveMisses(0);
    setResetFlash(true);
    setTimeout(() => setResetFlash(false), 800);
    const newOrder = shuffle([0, 1, 2, 3, 4]);
    setRightOrder(newOrder);
    /* Give a new preview after reset */
    setColorsHidden(false);
    setPreviewTime(PREVIEW_DURATION);
    let t = PREVIEW_DURATION;
    if (previewTimerRef.current) clearInterval(previewTimerRef.current);
    previewTimerRef.current = setInterval(() => {
      t -= 1;
      setPreviewTime(t);
      if (t <= 0) {
        clearInterval(previewTimerRef.current);
        previewTimerRef.current = null;
        setColorsHidden(true);
      }
    }, 1000);
  }, []);

  const handleRightClick = useCallback((rightIndex) => {
    if (!puzzleActive || solved || selectedLeft === null || isRightConnected(rightIndex)) return;
    if (previewTime > 0) return; /* Can't click during preview */

    const leftColor = WIRE_COLORS[selectedLeft].name;
    const rightColor = WIRE_COLORS[rightOrder[rightIndex]].name;

    if (leftColor === rightColor) {
      const newConns = [...connections, { left: selectedLeft, right: rightIndex }];
      setConnections(newConns);
      setSelectedLeft(null);
      setConsecutiveMisses(0); /* Reset streak on correct match */
      if (newConns.length === 5) {
        setSolved(true);
        setPuzzleActive(false);
        setSuccessPhase(1);
        setTimeout(() => setSuccessPhase(2), 500);
        setTimeout(() => setSuccessPhase(3), 1200);
        setTimeout(() => setSuccessPhase(4), 1800);
        setTimeout(() => setShowFinalLeon(true), 2200);
      }
    } else {
      setWrongPair({ left: selectedLeft, right: rightIndex });
      setMismatches(m => m + 1);
      setShaking(true);
      setTimeout(() => setShaking(false), 500);

      const newConsecutive = consecutiveMisses + 1;

      if (newConsecutive >= MAX_CONSECUTIVE_MISSES) {
        /* 3 consecutive misses — full reset */
        setTimeout(() => {
          setWrongPair(null);
          setSelectedLeft(null);
          doFullReset();
        }, 700);
      } else {
        setConsecutiveMisses(newConsecutive);
        /* Scramble unconnected ports */
        setTimeout(() => {
          setWrongPair(null);
          setSelectedLeft(null);
          setRightOrder(prev => scrambleUnconnected(prev, connections));
          setScrambleFlash(true);
          setTimeout(() => setScrambleFlash(false), 500);
        }, 600);
      }
    }
  }, [puzzleActive, solved, selectedLeft, connections, rightOrder, isRightConnected, previewTime, consecutiveMisses, doFullReset, scrambleUnconnected]);

  const handleFinalDismiss = useCallback(() => {
    setShowFinalLeon(false);
    onItemCollected?.(ITEMS.POWER_CELL);
    setShowProceed(true);
  }, [onItemCollected]);

  return (
    <div className="puzzle-container relative overflow-hidden" data-testid="puzzle-wire">
      <CircuitBackground />

      {/* Header */}
      <div className="text-center mb-5 relative z-10">
        <div className="flex items-center justify-center gap-2 mb-2">
          <div className="w-8 h-px bg-gradient-to-r from-transparent to-green-700/40" />
          <CircuitBoard size={12} className="text-green-500" />
          <p className="text-xs font-terminal tracking-[6px] text-green-500 uppercase">Checkpoint 09</p>
          <CircuitBoard size={12} className="text-green-500" />
          <div className="w-8 h-px bg-gradient-to-l from-transparent to-green-700/40" />
        </div>
        <h2 className="font-orbitron text-2xl tracking-wider text-gray-200 uppercase">Power Grid</h2>
        <p className="text-sm text-green-700 font-terminal mt-1">Emergency Restoration // Connect matching wires</p>
      </div>

      {/* Tutorial */}
      <AnimatePresence>
        {showTutorial && <WireTutorial onStart={handleTutorialStart} />}
      </AnimatePresence>

      {/* HUD */}
      {(puzzleActive || solved) && (
        <WireHUD
          connected={connections.length}
          total={5}
          mismatches={mismatches}
          consecutiveMisses={consecutiveMisses}
          previewTime={previewTime}
          colorsHidden={colorsHidden}
        />
      )}

      {/* Reset flash overlay */}
      <AnimatePresence>
        {resetFlash && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center pointer-events-none"
          >
            <motion.div
              initial={{ opacity: 0.4 }}
              animate={{ opacity: 0 }}
              transition={{ duration: 0.8 }}
              className="absolute inset-0 bg-red-600/20"
            />
            <motion.div
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 1.2, opacity: 0 }}
              className="flex items-center gap-3 bg-black/80 border border-red-500/50 rounded-lg px-6 py-3"
            >
              <RotateCcw size={16} className="text-red-400" />
              <span className="font-orbitron text-red-400 text-sm tracking-wider">SYSTEM RESET — ALL WIRES DISCONNECTED</span>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {(puzzleActive || solved) && (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="max-w-lg mx-auto relative z-10">
          {/* Preview countdown overlay */}
          <AnimatePresence>
            {previewTime > 0 && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="absolute inset-0 z-20 flex items-center justify-center pointer-events-none"
              >
                <motion.div
                  key={previewTime}
                  initial={{ scale: 1.5, opacity: 0 }}
                  animate={{ scale: 1, opacity: 0.6 }}
                  exit={{ scale: 0.5, opacity: 0 }}
                  className="font-orbitron text-6xl text-cyan-400"
                  style={{ textShadow: '0 0 30px rgba(34,211,238,0.5)' }}
                >
                  {previewTime}
                </motion.div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Scramble flash */}
          <AnimatePresence>
            {scrambleFlash && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="absolute -top-8 left-1/2 -translate-x-1/2 z-20 flex items-center gap-2 bg-yellow-950/60 border border-yellow-600/40 rounded px-3 py-1"
              >
                <Shuffle size={10} className="text-yellow-400" />
                <span className="text-xs font-terminal text-yellow-400 tracking-wider">PORTS SCRAMBLED</span>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Wire panel */}
          <div className={`hud-bracket bg-black/50 p-6 relative overflow-hidden ${shaking ? 'animate-shake' : ''}`}>
            <HUDCorners />

            {/* Panel header */}
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <Cable size={12} className="text-green-600" />
                <span className="text-xs font-terminal text-green-600 tracking-[3px]">JUNCTION BOX</span>
              </div>
              {selectedLeft !== null && previewTime <= 0 && (
                <motion.span
                  initial={{ opacity: 0, x: 10 }}
                  animate={{ opacity: 1, x: 0 }}
                  className="text-xs font-terminal text-yellow-400 tracking-wider"
                >
                  SELECT MATCHING PORT
                </motion.span>
              )}
            </div>

            {/* Wire board */}
            <div className="flex justify-between items-center relative" data-testid="wire-board">
              {/* Left endpoints */}
              <div className="flex flex-col gap-3">
                {WIRE_COLORS.map((wire, i) => {
                  const connected = isLeftConnected(i);
                  const isSelected = selectedLeft === i;
                  const isWrong = wrongPair?.left === i;
                  return (
                    <motion.button
                      key={`left-${i}`}
                      whileHover={!connected ? { scale: 1.1, x: 3 } : {}}
                      whileTap={!connected ? { scale: 0.95 } : {}}
                      onClick={() => handleLeftClick(i)}
                      className={`relative flex items-center gap-2 transition-all ${connected ? 'opacity-50' : ''}`}
                      disabled={connected || previewTime > 0}
                      data-testid={`wire-left-${i}`}
                    >
                      <span className="text-xs font-terminal w-12 text-right tracking-wider" style={{ color: wire.color }}>
                        {wire.name.toUpperCase()}
                      </span>
                      <motion.div
                        animate={isWrong ? { x: [-3, 3, -3, 0] } : {}}
                        className={`w-12 h-12 rounded-lg border-2 flex items-center justify-center transition-all relative overflow-hidden
                          ${isSelected ? 'scale-110' : ''}
                          ${connected ? 'border-green-500/50 bg-green-950/20' : ''}`}
                        style={{
                          borderColor: connected ? undefined : wire.color,
                          background: connected ? undefined : isSelected ? wire.bg : 'rgba(0,0,0,0.3)',
                          boxShadow: isSelected ? `0 0 15px ${wire.glow}` : connected ? '0 0 8px rgba(34,197,94,0.3)' : undefined
                        }}
                      >
                        {connected ? (
                          <Check size={16} className="text-green-400" />
                        ) : (
                          <Plug size={18} style={{ color: wire.color }} />
                        )}
                        {isSelected && (
                          <motion.div
                            animate={{ scale: [1, 1.5, 1], opacity: [0.5, 0, 0.5] }}
                            transition={{ duration: 1, repeat: Infinity }}
                            className="absolute inset-0 rounded-lg border"
                            style={{ borderColor: wire.color }}
                          />
                        )}
                      </motion.div>
                    </motion.button>
                  );
                })}
              </div>

              {/* Connection lines SVG */}
              <div className="flex-1 relative mx-4" style={{ minHeight: '320px' }}>
                <svg className="absolute inset-0 w-full h-full" viewBox="0 0 200 320" preserveAspectRatio="none">
                  <defs>
                    <pattern id="grid" width="20" height="20" patternUnits="userSpaceOnUse">
                      <path d="M 20 0 L 0 0 0 20" fill="none" stroke="rgba(0,255,65,0.05)" strokeWidth="0.5" />
                    </pattern>
                  </defs>
                  <rect width="200" height="320" fill="url(#grid)" />
                  {connections.map((conn, i) => {
                    const y1 = conn.left * 64 + 28;
                    const y2 = conn.right * 64 + 28;
                    const color = WIRE_COLORS[conn.left].color;
                    return (
                      <motion.g key={`${conn.left}-${conn.right}`}>
                        <motion.line
                          initial={{ pathLength: 0, opacity: 0 }}
                          animate={{ pathLength: 1, opacity: 0.3 }}
                          transition={{ duration: 0.4 }}
                          x1="0" y1={y1} x2="200" y2={y2}
                          stroke={color} strokeWidth="8" strokeLinecap="round"
                          style={{ filter: `blur(4px)` }}
                        />
                        <motion.line
                          initial={{ pathLength: 0, opacity: 0 }}
                          animate={{ pathLength: 1, opacity: 1 }}
                          transition={{ duration: 0.4 }}
                          x1="0" y1={y1} x2="200" y2={y2}
                          stroke={color} strokeWidth="3" strokeLinecap="round"
                          style={{ filter: `drop-shadow(0 0 4px ${color})` }}
                        />
                        <motion.circle
                          initial={{ cx: 0, cy: y1, opacity: 0 }}
                          animate={{ cx: [0, 200], cy: [y1, y2], opacity: [0, 1, 0] }}
                          transition={{ duration: 0.6, delay: 0.4 }}
                          r="4" fill={color}
                          style={{ filter: `drop-shadow(0 0 6px ${color})` }}
                        />
                      </motion.g>
                    );
                  })}
                </svg>
              </div>

              {/* Right endpoints — HIDDEN after preview */}
              <div className="flex flex-col gap-3">
                {rightOrder.map((colorIdx, i) => {
                  const wire = WIRE_COLORS[colorIdx];
                  const connected = isRightConnected(i);
                  const isWrong = wrongPair?.right === i;
                  const hidden = colorsHidden && !connected;
                  return (
                    <motion.button
                      key={`right-${i}`}
                      whileHover={!connected && selectedLeft !== null && !hidden ? {} : !connected && selectedLeft !== null ? { scale: 1.05, x: -2 } : {}}
                      whileTap={!connected ? { scale: 0.95 } : {}}
                      onClick={() => handleRightClick(i)}
                      className={`relative flex items-center gap-2 transition-all ${connected ? 'opacity-50' : ''} ${selectedLeft === null && !connected ? 'opacity-40' : ''}`}
                      disabled={connected || selectedLeft === null || previewTime > 0}
                      data-testid={`wire-right-${i}`}
                    >
                      <motion.div
                        animate={isWrong ? { x: [-3, 3, -3, 0] } : {}}
                        layout
                        className={`w-12 h-12 rounded-lg border-2 flex items-center justify-center transition-all relative overflow-hidden
                          ${connected ? 'border-green-500/50 bg-green-950/20' : ''}`}
                        style={{
                          borderColor: connected ? undefined : hidden ? '#374151' : wire.color,
                          background: connected ? undefined : hidden ? 'rgba(20,20,20,0.6)' : selectedLeft !== null ? wire.bg : 'rgba(0,0,0,0.3)',
                          boxShadow: hidden
                            ? (selectedLeft !== null ? '0 0 6px rgba(55,65,81,0.4)' : undefined)
                            : (selectedLeft !== null && !connected ? `0 0 10px ${wire.glow}` : connected ? '0 0 8px rgba(34,197,94,0.3)' : undefined)
                        }}
                      >
                        {connected ? (
                          <Check size={16} className="text-green-400" />
                        ) : hidden ? (
                          <span className="text-gray-500 font-orbitron text-lg">?</span>
                        ) : (
                          <Plug size={18} style={{ color: wire.color, transform: 'scaleX(-1)' }} />
                        )}
                      </motion.div>
                      <span className="text-xs font-terminal w-12 tracking-wider" style={{ color: hidden ? '#374151' : wire.color }}>
                        {hidden ? '??????' : wire.name.toUpperCase()}
                      </span>
                    </motion.button>
                  );
                })}
              </div>
            </div>

            {/* Wrong match warning */}
            <AnimatePresence>
              {wrongPair && (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0 }}
                  className="flex items-center justify-center gap-2 mt-4 bg-red-950/20 border border-red-500/30 rounded px-3 py-2"
                >
                  <AlertTriangle size={12} className="text-red-400" />
                  <span className="text-xs font-terminal text-red-400 tracking-wider">
                    COLOR MISMATCH — {consecutiveMisses + 1 >= MAX_CONSECUTIVE_MISSES ? 'SYSTEM RESETTING...' : 'PORTS SCRAMBLING...'}
                  </span>
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {/* Instructions */}
          {!solved && previewTime <= 0 && (
            <p className="text-center text-xs text-gray-600 font-rajdhani mt-4">
              {colorsHidden
                ? 'Right-side colors are hidden — match from memory'
                : 'Click a wire on the left, then click its matching color on the right'}
            </p>
          )}

          {/* Multi-phase success */}
          {solved && (
            <div className="text-center mt-6 relative">
              <AnimatePresence>
                {successPhase >= 1 && (
                  <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="relative">
                    <motion.div
                      initial={{ scale: 0.3, opacity: 0.8 }}
                      animate={{ scale: 3, opacity: 0 }}
                      transition={{ duration: 1, ease: 'easeOut' }}
                      className="absolute left-1/2 top-10 -translate-x-1/2 w-20 h-20 rounded-full border-2 border-green-400"
                    />
                    <motion.div
                      initial={{ opacity: 0.6 }}
                      animate={{ opacity: 0 }}
                      transition={{ duration: 0.5 }}
                      className="absolute inset-0 bg-green-400/10 rounded-lg"
                    />
                  </motion.div>
                )}
              </AnimatePresence>

              {successPhase >= 2 && (
                <div className="relative w-20 h-20 mx-auto mb-4">
                  <motion.div
                    animate={{ rotate: 360 }}
                    transition={{ duration: 3, repeat: Infinity, ease: 'linear' }}
                    className="absolute inset-0 border-2 border-dashed border-green-500/20 rounded-full"
                  />
                  <motion.div
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ type: 'spring', stiffness: 300, damping: 15 }}
                    className="absolute inset-0 flex items-center justify-center"
                  >
                    <div className="w-16 h-16 rounded-full bg-green-900/30 border-2 border-green-500 flex items-center justify-center"
                      style={{ boxShadow: '0 0 25px rgba(0,255,65,0.2)' }}>
                      <Zap size={28} className="text-green-400" />
                    </div>
                  </motion.div>
                </div>
              )}

              {successPhase >= 3 && (
                <motion.p
                  initial={{ letterSpacing: '2px', opacity: 0 }}
                  animate={{ letterSpacing: '5px', opacity: 1 }}
                  className="font-orbitron text-green-400 text-sm mb-3"
                >
                  POWER RESTORED
                </motion.p>
              )}

              {successPhase >= 4 && (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="space-y-2"
                >
                  <div className="flex items-center justify-center gap-6 text-xs font-terminal">
                    <span className="text-green-500">CONNECTIONS <span className="text-green-300 font-orbitron">5/5</span></span>
                    <span className="text-gray-500">FAULTS <span className={`font-orbitron ${mismatches === 0 ? 'text-green-300' : 'text-red-400'}`}>{mismatches}</span></span>
                  </div>
                  <p className="text-gray-400 font-rajdhani">Power Cell acquired</p>
                </motion.div>
              )}
            </div>
          )}

          {showProceed && (
            <motion.div
              initial={{ opacity: 0, y: 15 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ type: 'spring', stiffness: 200, damping: 20 }}
              className="text-center mt-8 relative z-20"
            >
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => onComplete?.()}
                className="inline-flex items-center gap-2 px-8 py-3 rounded font-orbitron text-sm tracking-widest text-black relative overflow-hidden"
                style={{ background: 'linear-gradient(135deg, #22c55e, #00ff41)' }}
              >
                <span className="relative z-10">PROCEED</span>
                <motion.div
                  className="absolute inset-0"
                  animate={{ x: ['-100%', '200%'] }}
                  transition={{ duration: 2, repeat: Infinity, ease: 'linear' }}
                  style={{ background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.15), transparent)', width: '50%' }}
                />
              </motion.button>
            </motion.div>
          )}
        </motion.div>
      )}

      {showLeon && (
        <LeonDialogue
          message={PUZZLE_5_WIRE_PUZZLE.leon.intro}
          onDismiss={handleLeonDismiss}
        />
      )}

      {showFinalLeon && (
        <LeonDialogue
          message={PUZZLE_5_WIRE_PUZZLE.leon.complete}
          onDismiss={handleFinalDismiss}
        />
      )}

      <style>{`
        @keyframes shake {
          0%, 100% { transform: translateX(0); }
          10%, 30%, 50%, 70%, 90% { transform: translateX(-3px); }
          20%, 40%, 60%, 80% { transform: translateX(3px); }
        }
        .animate-shake {
          animation: shake 0.5s ease-in-out;
        }
      `}</style>
    </div>
  );
}
